import React, { useState, useEffect, useCallback } from 'react';
import {
  fetchLooks, uploadCanvasBg,
  fetchGifCampaigns, createGifCampaign,
} from '../shared/api';
import { FAVORITES_SEASONS_WITH_NONE as SEASONS } from '../shared/seasons';

const Field = ({
  label, children, half, error,
}) => (
  <div style={{
    marginBottom: 12, display: 'inline-block', width: half ? '48%' : '100%', marginRight: half ? '4%' : 0, verticalAlign: 'top',
  }}
  >
    <label style={{
      display: 'block', fontSize: 11, color: error ? '#d32f2f' : '#666', marginBottom: 3, fontWeight: 500,
    }}
    >
      {label}
      {error ? ' *' : ''}
    </label>
    {children}
    {error && <div style={{ fontSize: 10, color: '#d32f2f', marginTop: 2 }}>{error}</div>}
  </div>
);

const Input = ({
  value, onChange, type = 'text', placeholder, error, onBlur, ...props
}) => (
  <input
    type={type}
    value={value ?? ''}
    onChange={(e) => onChange(type === 'number' ? (e.target.value === '' ? null : Number(e.target.value)) : e.target.value)}
    onKeyDown={type === 'number' ? (e) => {
      if (e.shiftKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
        e.preventDefault();
        const step = parseFloat(e.target.step) || 1;
        const current = parseFloat(e.target.value) || 0;
        const delta = (e.key === 'ArrowUp' ? 1 : -1) * step * 10;
        onChange(Math.round((current + delta) * 1e6) / 1e6);
      }
    } : undefined}
    onBlur={onBlur}
    placeholder={placeholder}
    style={{
      width: '100%',
      padding: '6px 8px',
      border: `1px solid ${error ? '#d32f2f' : '#ddd'}`,
      borderRadius: 3,
      fontSize: 13,
      boxSizing: 'border-box',
    }}
    {...props}
  />
);

const Select = ({ value, onChange, options }) => (
  <select
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
    style={{
      width: '100%',
      padding: '6px 8px',
      border: '1px solid #ddd',
      borderRadius: 3,
      fontSize: 13,
      background: '#fff',
      boxSizing: 'border-box',
    }}
  >
    {options.map((o) => (
      <option key={o.value} value={o.value}>{o.label}</option>
    ))}
  </select>
);

// SEASONS (incl. the leading "(none)" option) comes from the shared single
// source of truth (../shared/seasons); it must stay in sync with
// LookTemplate::SPEC_PREFIX (enforced by a guard spec).

const POSE_NUMBERS = Array.from({ length: 100 }, (_, i) => {
  const n = String(i + 1).padStart(2, '0');
  return { value: `-${n}`, label: `-${n}` };
});

// ---------------------------------------------------------------------------
// Senior promo-gif pose selection.
//
// Fall's fixed two-pose session lets a slide name a pose number outright. A
// senior session is 2..90+ poses, so a fixed number is wrong in BOTH
// directions: the pose may not exist for her -- and gif_creator drops the
// WHOLE slide when a declared source is missing, artwork included, so the
// frame vanishes rather than showing a gap -- or it names pose 10 of 60 and
// advertises the smallest slice of what she actually shot.
//
// A senior slot therefore stores a POSITION through her session, resolved to a
// real pose per student at render time.
const SENIOR_CANVAS = { width: 640, height: 640 };

const isSeniorSlide = (lookType, w, h) => lookType === 'gif_slide'
  && Number(w) === SENIOR_CANVAS.width && Number(h) === SENIOR_CANVAS.height;

const SENIOR_POSITIONS = [
  { value: 'auto', label: 'Auto — spaced across the campaign' },
  { value: '0', label: 'Her first pose' },
  { value: '33', label: 'A third of the way through' },
  { value: '50', label: 'Halfway through' },
  { value: '66', label: 'Two thirds of the way through' },
  { value: '100', label: 'Her last pose' },
];

// 'skip' does NOT leave an empty picture frame: gif_creator drops a slide whose
// declared source it cannot resolve, artwork and all. Labelled for what actually
// happens rather than what it sounds like.
const SENIOR_FALLBACKS = [
  { value: 'nearest', label: 'Use her nearest pose anyway' },
  { value: 'skip', label: 'Skip this slide for her' },
];

// Session lengths to preview against — the measured shape of a senior season:
// a fifth of seniors shoot exactly 2, the median is 10, the longest seen is 92.
const SENIOR_PREVIEW_COUNTS = [2, 3, 4, 12, 30, 92];

// Which pose a position lands on for a session of n poses, endpoints inclusive
// so 0% is always her first and 100% always her last. This is the EDITOR's
// preview, not the authority: the server-side resolver owns the real answer and
// the two have to be changed together.
const posePreviewFor = (pct, n) => Math.round((Number(pct) / 100) * (n - 1)) + 1;

const SOURCE_MODES = ['look', 'database', 'senior'];
const sourceModeOf = (photo) => (
  SOURCE_MODES.includes(photo.source_type) ? photo.source_type : 'look'
);

const SourceField = ({
  label, value, onChange, allLooks, error,
}) => {
  // Determine mode: if value is "1", "2", or "3" it's shot mode, otherwise look mode
  const isShot = value === '1' || value === '2' || value === '3' || value === 1 || value === 2 || value === 3;
  const [mode, setMode] = React.useState(isShot || value == null ? 'shot' : 'look');

  // Sync mode when value changes externally (e.g., switching to a different look)
  React.useEffect(() => {
    const nowShot = value === '1' || value === '2' || value === '3' || value === 1 || value === 2 || value === 3;
    setMode(nowShot || value == null ? 'shot' : 'look');
  }, [value]);

  const handleModeSwitch = (newMode) => {
    setMode(newMode);
    onChange(null); // clear on mode switch
  };

  return (
    <Field label={label} error={error}>
      <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
        <button
          type="button"
          onClick={() => handleModeSwitch('shot')}
          style={{
            padding: '3px 10px',
            fontSize: 11,
            border: '1px solid #ccc',
            borderRadius: 3,
            background: mode === 'shot' ? '#1976d2' : '#fff',
            color: mode === 'shot' ? '#fff' : '#666',
            cursor: 'pointer',
          }}
        >
          Shot #
        </button>
        <button
          type="button"
          onClick={() => handleModeSwitch('look')}
          style={{
            padding: '3px 10px',
            fontSize: 11,
            border: '1px solid #ccc',
            borderRadius: 3,
            background: mode === 'look' ? '#1976d2' : '#fff',
            color: mode === 'look' ? '#fff' : '#666',
            cursor: 'pointer',
          }}
        >
          Look Name
        </button>
      </div>
      {mode === 'shot' ? (
        <select
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
          style={{
            width: '100%',
            padding: '6px 8px',
            border: `1px solid ${error ? '#d32f2f' : '#ddd'}`,
            borderRadius: 3,
            fontSize: 13,
            background: '#fff',
            boxSizing: 'border-box',
          }}
        >
          <option value="">(none)</option>
          <option value="1">Shot 1</option>
          <option value="2">Shot 2</option>
          <option value="3">Shot 3</option>
        </select>
      ) : (
        <input
          type="text"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
          placeholder="e.g. SPR26-04"
          list="look-names-list"
          style={{
            width: '100%',
            padding: '6px 8px',
            border: `1px solid ${error ? '#d32f2f' : '#ddd'}`,
            borderRadius: 3,
            fontSize: 13,
            boxSizing: 'border-box',
          }}
        />
      )}
      {mode === 'look' && allLooks && (
        <datalist id="look-names-list">
          {allLooks.map((l) => <option key={l.id} value={l.name} />)}
        </datalist>
      )}
    </Field>
  );
};

const CANVAS_PRESETS = [
  { label: 'Portrait 2400x3000', width: 2400, height: 3000 },
  { label: 'Landscape 3000x2400', width: 3000, height: 2400 },
  { label: 'Square 2400x2400', width: 2400, height: 2400 },
  { label: 'GIF Slide 640x480 (fits MMS)', width: 640, height: 480 },
  // Square, for the senior promo gifs: a senior's session is a variable number
  // of poses (2..90+) rather than fall's fixed two, so the artwork carries a
  // pose grid rather than one portrait, and square suits it better than 4:3.
  // NB the preset selector matches on width/height alone, so there can only be
  // ONE 640x640 entry -- this is it, and any existing 640x640 look displays
  // under this label.
  { label: 'Senior GIF Slide 640x640', width: 640, height: 640 },
  { label: 'GIF Slide 800x600 (too big to text)', width: 800, height: 600 },
  { label: 'Custom', width: null, height: null },
];

// Seeded onto a NEW gif slide that has no dimensions yet.
//
// 640x480, not the 800x600 this used to be: a GIF's file size is essentially
// slides x canvas pixels, and 800x600 puts a 3-slide campaign around 680 KB —
// past the 600 KB an MMS can carry, so the text arrives with no picture at all.
// The same campaign at 640x480 lands near 480 KB. Every slide in one campaign
// must agree, because EC2 takes the gif's dimensions from the FIRST slide only
// (gif_creator.py) and forces every later frame into that shape; changing the
// canvas on any slide therefore moves the whole campaign (PromoGifCanvasResize).
const GIF_SLIDE_DEFAULT_CANVAS = { width: 640, height: 480 };

// Inline creator for a new PromoGifCampaign, opened from the LookTab's
// campaign picker so a designer never has to leave the editor. Fields
// mirror the server contract exactly (slug regex + campaign_name uniqueness
// are enforced in the model).
const NewCampaignModal = ({
  season, campaigns, onCreated, onClose,
}) => {
  const [form, setForm] = useState({
    season: season || '',
    slug: '',
    name: '',
    campaign_name: '',
    spec_s3_key: '',
    output_prefix: '',
    send_offset_days: '',
    variant_of_id: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const update = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const handleSubmit = async () => {
    if (!form.slug || !form.name || !form.campaign_name || !form.spec_s3_key || !form.output_prefix) {
      setError('slug, name, campaign_name, spec_s3_key, and output_prefix are required');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const payload = { ...form };
      payload.send_offset_days = payload.send_offset_days === '' ? null : Number(payload.send_offset_days);
      payload.variant_of_id = payload.variant_of_id === '' ? null : Number(payload.variant_of_id);
      const created = await createGifCampaign(payload);
      onCreated(created);
    } catch (e) {
      setError(e.response?.data?.errors?.join(', ') || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,.5)',
        zIndex: 10001,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 8, padding: 20, width: 460, maxWidth: '95%',
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 10 }}>New Promo GIF Campaign</div>
        <Field label="Season">
          <Select value={form.season} onChange={(v) => update('season', v)} options={SEASONS} />
        </Field>
        <Field label="Slug (lowercase, e.g. jd3, black_friday)" half>
          <Input value={form.slug} onChange={(v) => update('slug', v)} placeholder="jd3" />
        </Field>
        <Field label="Send offset days (blank for events)" half>
          <Input type="number" value={form.send_offset_days} onChange={(v) => update('send_offset_days', v)} placeholder="3" />
        </Field>
        <Field label="Human name">
          <Input value={form.name} onChange={(v) => update('name', v)} placeholder="Fall JD+3" />
        </Field>
        <Field label="Campaign name (shown in Contact Data dropdown)">
          <Input value={form.campaign_name} onChange={(v) => update('campaign_name', v)} placeholder="Fall2027_JD3" />
          {form.variant_of_id && (
            <div style={{ fontSize: 11, color: '#777', marginTop: 2 }}>
              Ignored for a variant — it reports the parent&apos;s campaign name, so both
              groups of students land under one key.
            </div>
          )}
        </Field>
        <Field label="Spec S3 key">
          <Input value={form.spec_s3_key} onChange={(v) => update('spec_s3_key', v)} placeholder="specs/2027/Claude Specs_ Fall 2027 - U2027 Promo Gif JD3.csv" />
        </Field>
        <Field label="EC2 output prefix">
          <Input value={form.output_prefix} onChange={(v) => update('output_prefix', v)} placeholder="images/promotional-images/fall2027_hs_ms_jd3/" />
        </Field>
        <Field label="Variant of (optional)">
          <select
            value={form.variant_of_id}
            onChange={(e) => update('variant_of_id', e.target.value)}
            style={{
              width: '100%', padding: '6px 8px', border: '1px solid #ddd', borderRadius: 3, fontSize: 13,
            }}
          >
            <option value="">Not a variant — a campaign of its own</option>
            {(campaigns || [])
              .filter((c) => !c.variant_of_id && c.season === form.season)
              .map((c) => (
                <option key={c.id} value={String(c.id)}>{c.name}</option>
              ))}
          </select>
          <div style={{ fontSize: 11, color: '#777', marginTop: 2 }}>
            A second artwork for students the parent&apos;s slides cannot source — a sport
            player with only a team photo, whose landscape picture needs its own slides.
            Enqueue it from the EC2 Processor panel&apos;s fallback picker.
          </div>
        </Field>
        {error && <div style={{ color: '#d32f2f', fontSize: 12, marginBottom: 8 }}>{error}</div>}
        <div style={{
          display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 6,
        }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '6px 12px',
              border: '1px solid #ccc',
              borderRadius: 3,
              background: '#fff',
              cursor: 'pointer',
              fontSize: 12,
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={busy}
            style={{
              padding: '6px 16px',
              border: 'none',
              borderRadius: 3,
              background: busy ? '#9e9e9e' : '#1976d2',
              color: '#fff',
              cursor: busy ? 'progress' : 'pointer',
              fontSize: 12,
              fontWeight: 500,
            }}
          >
            {busy ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Season-scoped campaign picker with an inline "+ New" that opens
// NewCampaignModal. Emits the selected campaign_id; the caller is
// responsible for writing it onto the look form.
const GifCampaignPicker = ({
  season, value, onChange, error,
}) => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(async () => {
    if (!season) { setCampaigns([]); return; }
    setLoading(true);
    try {
      const data = await fetchGifCampaigns(season);
      setCampaigns(data);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to load gif campaigns:', e);
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  }, [season]);

  useEffect(() => { load(); }, [load]);

  return (
    <Field label="Promo GIF Campaign" error={error}>
      <div style={{ display: 'flex', gap: 6 }}>
        <select
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
          disabled={!season || loading}
          style={{
            flex: 1,
            padding: '6px 8px',
            border: `1px solid ${error ? '#d32f2f' : '#ddd'}`,
            borderRadius: 3,
            fontSize: 13,
            background: '#fff',
          }}
        >
          <option value="">{loading ? 'Loading…' : season ? 'Select a campaign' : 'Pick a season first'}</option>
          {campaigns.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
              {' '}
              —
              {c.slug}
              {' '}
              (
              {c.slide_count}
              {' '}
              slide
              {c.slide_count === 1 ? '' : 's'}
              )
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => setShowNew(true)}
          disabled={!season}
          style={{
            padding: '6px 10px',
            border: '1px solid #1976d2',
            borderRadius: 3,
            background: '#e3f2fd',
            color: '#1976d2',
            cursor: season ? 'pointer' : 'not-allowed',
            fontSize: 12,
            whiteSpace: 'nowrap',
            opacity: season ? 1 : 0.5,
          }}
        >
          + New
        </button>
      </div>
      {showNew && (
        <NewCampaignModal
          season={season}
          campaigns={campaigns}
          onCreated={(c) => {
            setCampaigns((prev) => [...prev, { ...c, slide_count: 0 }]);
            onChange(c.id);
            setShowNew(false);
          }}
          onClose={() => setShowNew(false)}
        />
      )}
    </Field>
  );
};

// --- Collage Photos Section ---

// " — collage, horizontal" style suffix for a look option, so a list that now
// mixes singles and collages in both orientations stays readable. Empty for a
// plain vertical single (the common case) to keep those options uncluttered.
const describeLook = (look) => {
  const notes = [];
  if (look.look_type && look.look_type !== 'single') notes.push(look.look_type);
  if (look.canvas_width && look.canvas_height && look.canvas_width > look.canvas_height) {
    notes.push('horizontal');
  }
  return notes.length ? ` — ${notes.join(', ')}` : '';
};

const CollagePhotoSlot = ({
  photo, index, onChange, onRemove, allLooks, isSelected, onSelect, canvasWidth, canvasHeight,
  parentLookType,
}) => {
  const [seasonLooks, setSeasonLooks] = useState([]);
  const [loadingLooks, setLoadingLooks] = useState(false);
  // Source mode: 'look' for designer look templates, 'database' for student's
  // pose by season+number, 'senior' for a position through her session that is
  // resolved to a pose per student (see isSeniorSlide).
  const sourceMode = sourceModeOf(photo);
  const seniorSlide = isSeniorSlide(parentLookType, canvasWidth, canvasHeight);

  // Which look types may be referenced from this slot depends on what we're
  // building — the two consumers resolve `look_ref` by different contracts:
  //
  //   gif_slide -> GifSpecExport writes the ref to the `source_image` column
  //     and EC2's gif_creator._resolve_source turns it into
  //     `{base_name}-NN.png` in the pngmed bucket, purely by pose number. Any
  //     pose EC2 renders for the season qualifies, so a COLLAGE look (which is
  //     how the horizontal E2027 looks are authored) works exactly as well as
  //     a single. Only another gif_slide is excluded — slides aren't sellable
  //     poses and are deliberately kept out of image_processing_specs, so a
  //     slide can never be a slide's source.
  //
  //   collage -> the favorites CSV's `photo_N_id`, which EC2 resolves from the
  //     member poses rendered earlier in the same run. A collage referencing
  //     another collage would depend on render ordering within collage_rows,
  //     which is not a contract we have. Singles only there.
  const eligible = useCallback((l) => (
    parentLookType === 'gif_slide'
      ? l.look_type !== 'gif_slide'
      : l.look_type === 'single'
  ), [parentLookType]);

  // Fetch looks when season changes — filtered by season if one is selected
  useEffect(() => {
    if (sourceMode !== 'look') { setSeasonLooks([]); return; }
    if (allLooks) {
      const usable = allLooks.filter(eligible);
      setSeasonLooks(photo.season ? usable.filter((l) => l.season === photo.season) : usable);
      // If no looks match the selected season, show all seasons so the user can still pick one
      if (photo.season && usable.length > 0 && usable.filter((l) => l.season === photo.season).length === 0) {
        setSeasonLooks(usable);
      }
    } else {
      setLoadingLooks(true);
      fetchLooks(photo.season || undefined).then((looks) => {
        setSeasonLooks(looks.filter(eligible));
      }).catch(() => setSeasonLooks([]))
        .finally(() => setLoadingLooks(false));
    }
  }, [photo.season, allLooks, sourceMode, eligible]);

  const handleSourceToggle = (mode) => {
    const next = {
      ...photo, source_type: mode, look_ref: null, pose_number: null,
    };
    if (mode === 'senior') {
      // Seed the defaults so a freshly-switched slot is already valid rather
      // than half-configured, and give it a placement like the other modes do.
      next.senior_position = photo.senior_position ?? 'auto';
      next.yearbook_only = photo.yearbook_only ?? false;
      next.no_eligible_fallback = photo.no_eligible_fallback ?? 'nearest';
      if (photo.x == null) setDefaultPosition(next, 0.8);
    } else {
      next.senior_position = null;
      next.yearbook_only = null;
      next.no_eligible_fallback = null;
    }
    onChange(next);
  };

  const handleSeasonChange = (season) => {
    onChange({
      ...photo, season, look_ref: null, pose_number: null,
    });
  };

  const setDefaultPosition = (updates, aspectRatio) => {
    if (updates.x != null) return; // already positioned
    const cw = canvasWidth || 2400;
    const ch = canvasHeight || 3000;
    const defaultH = 0.4;
    const defaultW = defaultH * (aspectRatio || 0.8) * (ch / cw);
    updates.height = Math.min(defaultH, 0.9);
    updates.width = Math.min(defaultW, 0.9);
    updates.x = Math.max(0, 0.5 - updates.width / 2);
    updates.y = Math.max(0, 0.5 - updates.height / 2);
  };

  const handleLookChange = (look_ref) => {
    const selectedLook = seasonLooks.find((l) => l.name === look_ref);
    const updates = {
      ...photo, look_ref, source_type: 'look', pose_number: null,
    };
    if (look_ref && photo.x == null) {
      const lookAspect = selectedLook ? (selectedLook.canvas_width || 2400) / (selectedLook.canvas_height || 3000) : 0.8;
      setDefaultPosition(updates, lookAspect);
    }
    onChange(updates);
  };

  const handlePoseNumberChange = (pose_number) => {
    const updates = {
      ...photo,
      source_type: 'database',
      look_ref: null,
      pose_number: pose_number || null,
    };
    if (pose_number && photo.x == null) {
      setDefaultPosition(updates, 0.8);
    }
    onChange(updates);
  };

  const handleSeniorPatch = (p) => onChange({ ...photo, ...p });

  // Degrees clockwise, matching what the EC2 spec carries. Clamped to the
  // slider's range so a typed value cannot put the photo somewhere the
  // designer could not have dragged it to, and stored as a NUMBER so the
  // export's blank-when-zero rule sees a real 0 rather than the string '0'.
  const rotation = Number(photo.rotation) || 0;
  const handleRotate = (value) => {
    const degrees = Math.max(-45, Math.min(45, Math.round(Number(value) || 0)));
    onChange({ ...photo, rotation: degrees });
  };

  // Typed size, as a percentage of the canvas. The drag handles preserve the
  // box's aspect ratio on every path (corner AND edge, see useCanvasDrag), so
  // before this there was no way at all to change a box's SHAPE -- a portrait
  // slot could only ever become a bigger or smaller portrait. That stranded
  // the sports team slide: SPORTS26-04 is landscape 800x640 and the slot was
  // portrait, so cover-crop binned a third of the team and the only fix was
  // hand-editing jsonb in the SQL console.
  //
  // Held as a DRAFT STRING while typing and committed on blur or Enter.
  // Clamping each keystroke made the field unusable: typing "15" clamps the
  // leading "1" to the 2% minimum, re-renders as "2.0", and the next
  // keystroke reads "2.05". Rounding to one decimal on every render broke
  // decimals the same way ("12" became "12.0", so ".5" gave "12.05").
  const [sizeDraft, setSizeDraft] = React.useState({});

  // The draft while the field is being edited, otherwise the stored value --
  // so a drag that changes the box resyncs the display with no extra wiring.
  const sizeValue = (dimension) => (
    sizeDraft[dimension] !== undefined
      ? sizeDraft[dimension]
      : ((photo[dimension] || 0) * 100).toFixed(1)
  );

  const commitSize = (dimension) => () => {
    const raw = sizeDraft[dimension];
    setSizeDraft((d) => {
      const next = { ...d };
      delete next[dimension];
      return next;
    });
    // Empty or unparseable commits NOTHING rather than a value: a number
    // input hands back '' for both, and Number('') is 0, which would collapse
    // the box to the 2% minimum.
    if (raw === undefined || raw === '') return;
    const pct = Number(raw);
    if (!Number.isFinite(pct)) return;
    onChange({ ...photo, [dimension]: Math.max(0.02, Math.min(1, pct / 100)) });
  };

  const seniorPosition = photo.senior_position ?? 'auto';
  const seniorPositionLabel = SENIOR_POSITIONS
    .find((o) => o.value === String(seniorPosition))?.label
    ?? `${seniorPosition}% through her session`;

  const hasPlacement = photo.x != null;
  let displayName = photo.look_ref;
  if (sourceMode === 'database') {
    displayName = photo.pose_number ? `${photo.season || ''} ${photo.pose_number}` : null;
  } else if (sourceMode === 'senior') {
    displayName = `${seniorPositionLabel}${photo.yearbook_only ? ' · yearbook only' : ''}`;
  }

  return (
    <div
      onClick={() => onSelect(index)}
      style={{
        padding: '8px 10px',
        marginBottom: 8,
        border: `2px solid ${isSelected ? '#1976d2' : '#e0e0e0'}`,
        borderRadius: 6,
        background: isSelected ? '#e3f2fd' : '#fff',
        cursor: 'pointer',
        position: 'relative',
      }}
    >
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6,
      }}
      >
        <span style={{ fontSize: 12, fontWeight: 600, color: '#333' }}>
          Photo
          {index + 1}
        </span>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onRemove(index); }}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: 14,
            color: '#999',
            lineHeight: 1,
            padding: '2px 4px',
          }}
          title="Remove photo"
        >
          &times;
        </button>
      </div>

      {/* Source toggle */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); handleSourceToggle('look'); }}
          style={{
            padding: '3px 10px',
            fontSize: 10,
            border: '1px solid #ccc',
            borderRadius: 3,
            background: sourceMode === 'look' ? '#1976d2' : '#fff',
            color: sourceMode === 'look' ? '#fff' : '#666',
            cursor: 'pointer',
          }}
        >
          Look Template
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); handleSourceToggle('database'); }}
          style={{
            padding: '3px 10px',
            fontSize: 10,
            border: '1px solid #ccc',
            borderRadius: 3,
            background: sourceMode === 'database' ? '#e65100' : '#fff',
            color: sourceMode === 'database' ? '#fff' : '#666',
            cursor: 'pointer',
          }}
        >
          Season Pose
        </button>
        {seniorSlide && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); handleSourceToggle('senior'); }}
            style={{
              padding: '3px 10px',
              fontSize: 10,
              border: '1px solid #ccc',
              borderRadius: 3,
              background: sourceMode === 'senior' ? '#6a1b9a' : '#fff',
              color: sourceMode === 'senior' ? '#fff' : '#666',
              cursor: 'pointer',
            }}
          >
            Senior Pose Rule
          </button>
        )}
      </div>

      {/* A slot configured for seniors on a canvas that is no longer the senior
          preset: the controls are hidden but the data is still there, so say so
          rather than letting it look like an ordinary empty slot. */}
      {sourceMode === 'senior' && !seniorSlide && (
        <div style={{
          marginBottom: 6,
          padding: 6,
          background: '#fff8e1',
          border: '1px solid #ffb300',
          borderRadius: 3,
          fontSize: 10,
          color: '#e65100',
        }}
        >
          This photo uses a senior pose rule, but the canvas is
          {' '}
          {canvasWidth}
          ×
          {canvasHeight}
          , not the
          {' '}
          {SENIOR_CANVAS.width}
          ×
          {SENIOR_CANVAS.height}
          {' '}
          senior preset. Switch the canvas back to edit the rule.
        </div>
      )}

      <Field label="Season" half>
        <Select
          value={photo.season || ''}
          onChange={handleSeasonChange}
          options={SEASONS}
        />
      </Field>

      {sourceMode === 'look' && (
        <Field label="Look" half>
          <select
            value={photo.look_ref || ''}
            onChange={(e) => handleLookChange(e.target.value || null)}
            disabled={loadingLooks}
            style={{
              width: '100%',
              padding: '6px 8px',
              border: '1px solid #ddd',
              borderRadius: 3,
              fontSize: 13,
              background: '#fff',
              boxSizing: 'border-box',
              opacity: loadingLooks ? 0.5 : 1,
            }}
          >
            <option value="">{loadingLooks ? 'Loading...' : 'Select a look'}</option>
            {seasonLooks.map((l) => (
              <option key={l.id || l.name} value={l.name}>
                {l.name}
                {describeLook(l)}
                {l.season !== photo.season ? ` (${l.season})` : ''}
              </option>
            ))}
          </select>
        </Field>
      )}

      {sourceMode === 'database' && (
        <Field label="Pose Number (e.g. -01)" half>
          <select
            value={photo.pose_number || ''}
            onChange={(e) => handlePoseNumberChange(e.target.value || null)}
            disabled={!photo.season}
            style={{
              width: '100%',
              padding: '6px 8px',
              border: '1px solid #ddd',
              borderRadius: 3,
              fontSize: 13,
              background: '#fff',
              boxSizing: 'border-box',
              opacity: !photo.season ? 0.5 : 1,
            }}
          >
            <option value="">{photo.season ? 'Select pose number' : 'Select season first'}</option>
            {POSE_NUMBERS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </Field>
      )}

      {sourceMode === 'database' && photo.pose_number && photo.season && (
        <div style={{
          marginTop: 4,
          padding: 6,
          background: '#fff3e0',
          borderRadius: 3,
          fontSize: 10,
          color: '#e65100',
        }}
        >
          At render time, each student's
          {' '}
          {photo.season}
          {' '}
          image ending in
          {' '}
          {photo.pose_number}
          .png will be placed here.
        </div>
      )}

      {sourceMode === 'senior' && seniorSlide && (
        <>
          <Field label="Which pose">
            <Select
              value={String(seniorPosition)}
              onChange={(v) => handleSeniorPatch({ senior_position: v })}
              options={SENIOR_POSITIONS}
            />
          </Field>

          <label
            style={{
              display: 'flex', alignItems: 'flex-start', gap: 6,
              fontSize: 11, color: '#333', cursor: 'pointer', marginBottom: 8,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="checkbox"
              checked={!!photo.yearbook_only}
              onChange={(e) => handleSeniorPatch({ yearbook_only: e.target.checked })}
              style={{ marginTop: 2 }}
            />
            <span>
              <strong>Yearbook-eligible poses only</strong>
              <span style={{ display: 'block', fontSize: 10, color: '#777', marginTop: 1 }}>
                Tuxedo or black drape. Excludes cap &amp; gown and street clothes.
                About half of all senior poses qualify.
              </span>
            </span>
          </label>

          {photo.yearbook_only && (
            <Field label="If she has no eligible pose">
              <Select
                value={photo.no_eligible_fallback || 'nearest'}
                onChange={(v) => handleSeniorPatch({ no_eligible_fallback: v })}
                options={SENIOR_FALLBACKS}
              />
            </Field>
          )}

          <div style={{
            marginTop: 4,
            padding: 6,
            background: '#f3e5f5',
            borderRadius: 3,
            fontSize: 10,
            color: '#6a1b9a',
          }}
          >
            {seniorPosition === 'auto' ? (
              <span>
                At render time this photo takes its position from where this slide
                sits among the campaign&apos;s auto-spaced photos, then resolves to
                a real pose in each student&apos;s session.
              </span>
            ) : (
              <>
                <div style={{ marginBottom: 4 }}>
                  <strong>{seniorPositionLabel}</strong>
                  {' '}
                  resolves to:
                </div>
                <table style={{ borderCollapse: 'collapse', fontSize: 10, width: '100%' }}>
                  <tbody>
                    <tr>
                      <td style={{ color: '#8e6ca0', paddingRight: 6 }}>She shot</td>
                      {SENIOR_PREVIEW_COUNTS.map((n) => (
                        <td key={n} style={{ textAlign: 'right', paddingRight: 6, color: '#8e6ca0' }}>{n}</td>
                      ))}
                    </tr>
                    <tr>
                      <td style={{ paddingRight: 6 }}>Uses pose</td>
                      {SENIOR_PREVIEW_COUNTS.map((n) => (
                        <td key={n} style={{ textAlign: 'right', paddingRight: 6, fontWeight: 700 }}>
                          {posePreviewFor(seniorPosition, n)}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
                {photo.yearbook_only && (
                  <div style={{ marginTop: 4, color: '#8e6ca0' }}>
                    Then snapped to the nearest yearbook-eligible pose, so the
                    actual pose may differ from the row above.
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      {hasPlacement && (
        <Field label="Tilt">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="range"
              min={-45}
              max={45}
              step={1}
              value={rotation}
              onChange={(e) => handleRotate(e.target.value)}
              style={{ flex: 1 }}
            />
            <input
              type="number"
              value={rotation}
              onChange={(e) => handleRotate(e.target.value)}
              aria-label="Tilt degrees"
              style={{
                width: 56,
                fontSize: 11,
                padding: '2px 4px',
                border: '1px solid #ccc',
                borderRadius: 3,
              }}
            />
            <span style={{ fontSize: 11, color: '#888' }}>°</span>
            {rotation !== 0 && (
              <button
                type="button"
                onClick={() => handleRotate(0)}
                title="Straighten"
                style={{
                  fontSize: 10,
                  padding: '2px 6px',
                  cursor: 'pointer',
                  border: '1px solid #ccc',
                  borderRadius: 3,
                  background: '#fff',
                }}
              >
                Reset
              </button>
            )}
          </div>
          <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>
            Positive tilts clockwise. The whole photo turns and stays visible —
            the corners it leaves show the artwork behind it.
          </div>
        </Field>
      )}

      {hasPlacement && (
        <Field label="Size (% of canvas)">
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              type="number"
              step={0.1}
              min={2}
              max={100}
              value={sizeValue('width')}
              onChange={(e) => {
                // Read the value BEFORE the updater runs: React 17 still
                // pools synthetic events, so e.target is unreliable inside a
                // deferred callback -- the draft silently stayed undefined
                // and the field kept re-rendering the stored value.
                const next = e.target.value;
                setSizeDraft((d) => ({ ...d, width: next }));
              }}
              onBlur={commitSize('width')}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                // Commit outright rather than relying on blur() to do it:
                // Enter should save whether or not the field holds focus.
                // The draft is cleared by the commit, so the blur that
                // follows is a no-op rather than a second write.
                commitSize('width')();
                e.target.blur();
              }}
              aria-label="Width percent"
              style={{
                width: 70, fontSize: 11, padding: '3px 4px', border: '1px solid #ddd', borderRadius: 3,
              }}
            />
            <span style={{ fontSize: 11, color: '#888' }}>w &times;</span>
            <input
              type="number"
              step={0.1}
              min={2}
              max={100}
              value={sizeValue('height')}
              onChange={(e) => {
                // Read the value BEFORE the updater runs: React 17 still
                // pools synthetic events, so e.target is unreliable inside a
                // deferred callback -- the draft silently stayed undefined
                // and the field kept re-rendering the stored value.
                const next = e.target.value;
                setSizeDraft((d) => ({ ...d, height: next }));
              }}
              onBlur={commitSize('height')}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                // Commit outright rather than relying on blur() to do it:
                // Enter should save whether or not the field holds focus.
                // The draft is cleared by the commit, so the blur that
                // follows is a no-op rather than a second write.
                commitSize('height')();
                e.target.blur();
              }}
              aria-label="Height percent"
              style={{
                width: 70, fontSize: 11, padding: '3px 4px', border: '1px solid #ddd', borderRadius: 3,
              }}
            />
            <span style={{ fontSize: 11, color: '#888' }}>h</span>
          </div>
          <div style={{ fontSize: 11, color: '#777', marginTop: 2 }}>
            Dragging a handle keeps the current shape. Type here and press Enter
            to change it &mdash;
            match the source&apos;s shape or the photo is centre-cropped to fit
            (a landscape team picture in a portrait slot loses both ends).
          </div>
        </Field>
      )}

      {hasPlacement && displayName && (
        <div style={{ fontSize: 10, color: '#888', marginTop: 4 }}>
          {sourceMode === 'database' && <span style={{ color: '#e65100', fontWeight: 500 }}>Pose </span>}
          Position: (
          {(photo.x * 100).toFixed(1)}
          %,
          {' '}
          {(photo.y * 100).toFixed(1)}
          %)
        </div>
      )}
    </div>
  );
};

const CollagePhotosSection = ({
  photos, onChange, allLooks, selectedPhotoIndex, onSelectPhoto, canvasWidth, canvasHeight,
  lookType,
}) => {
  const [dragFrom, setDragFrom] = useState(null);

  const updatePhoto = (index, updated) => {
    const next = [...photos];
    next[index] = updated;
    onChange(next);
  };

  const removePhoto = (index) => {
    const next = photos.filter((_, i) => i !== index);
    onChange(next);
    // Adjust selection
    if (selectedPhotoIndex === index) onSelectPhoto(null);
    else if (selectedPhotoIndex > index) onSelectPhoto(selectedPhotoIndex - 1);
  };

  const addPhoto = () => {
    onChange([...photos, {
      look_ref: null, season: null, x: null, y: null, width: null, height: null,
    }]);
  };

  // Layer reorder via drag
  const handleDragStart = (e, index) => {
    e.dataTransfer.effectAllowed = 'move';
    setDragFrom(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, toIndex) => {
    e.preventDefault();
    if (dragFrom === null || dragFrom === toIndex) return;
    const next = [...photos];
    const [moved] = next.splice(dragFrom, 1);
    next.splice(toIndex, 0, moved);
    onChange(next);
    // Update selection to follow the moved item
    if (selectedPhotoIndex === dragFrom) onSelectPhoto(toIndex);
    else if (selectedPhotoIndex > dragFrom && selectedPhotoIndex <= toIndex) onSelectPhoto(selectedPhotoIndex - 1);
    else if (selectedPhotoIndex < dragFrom && selectedPhotoIndex >= toIndex) onSelectPhoto(selectedPhotoIndex + 1);
    setDragFrom(null);
  };

  return (
    <div>
      <h4 style={{ margin: '16px 0 8px', fontSize: 14, color: '#333' }}>Photos on Canvas</h4>
      <p style={{ fontSize: 10, color: '#999', margin: '0 0 8px' }}>
        Select looks to composite onto the canvas. Drag to reorder layers (bottom = back, top = front).
      </p>

      {photos.map((photo, i) => (
        <div
          key={i}
          draggable
          onDragStart={(e) => handleDragStart(e, i)}
          onDragOver={(e) => handleDragOver(e, i)}
          onDrop={(e) => handleDrop(e, i)}
          style={{
            display: 'flex', alignItems: 'stretch', gap: 4, marginBottom: 2,
          }}
        >
          <div
            style={{
              width: 16,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'grab',
              color: '#bbb',
              fontSize: 14,
              flexShrink: 0,
              userSelect: 'none',
            }}
            title="Drag to reorder layer"
          >
            ⋮⋮
          </div>
          <div style={{ flex: 1 }}>
            <CollagePhotoSlot
              photo={photo}
              index={i}
              onChange={(updated) => updatePhoto(i, updated)}
              onRemove={removePhoto}
              allLooks={allLooks}
              isSelected={selectedPhotoIndex === i}
              onSelect={onSelectPhoto}
              canvasWidth={canvasWidth}
              canvasHeight={canvasHeight}
              parentLookType={lookType}
            />
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={addPhoto}
        style={{
          width: '100%',
          padding: '8px',
          border: '1px dashed #bbb',
          borderRadius: 4,
          background: '#fafafa',
          cursor: 'pointer',
          fontSize: 12,
          color: '#666',
          marginTop: 4,
        }}
      >
        + Add Look to Canvas
      </button>
    </div>
  );
};

const LookTab = ({
  formData, onChange, validationErrors, allLooks, referencePoses,
}) => {
  const [nameWarning, setNameWarning] = useState(null);
  const [selectedPreset, setSelectedPreset] = useState(() => {
    const match = CANVAS_PRESETS.findIndex((p) => p.width === formData.canvas_width && p.height === formData.canvas_height);
    return match >= 0 ? match : CANVAS_PRESETS.length - 1; // default to Custom
  });

  // Sync preset when formData changes externally (e.g., loading a look)
  useEffect(() => {
    const match = CANVAS_PRESETS.findIndex((p) => p.width === formData.canvas_width && p.height === formData.canvas_height);
    setSelectedPreset(match >= 0 ? match : CANVAS_PRESETS.length - 1);
  }, [formData.canvas_width, formData.canvas_height]);

  const handlePresetChange = (idx) => {
    setSelectedPreset(idx);
    const preset = CANVAS_PRESETS[idx];
    if (preset.width != null && preset.height != null) {
      onChange({ canvas_width: preset.width, canvas_height: preset.height });
    }
  };

  const handleWidthChange = (v) => {
    onChange({ canvas_width: v });
    // Switch to Custom if it no longer matches a preset
    const match = CANVAS_PRESETS.findIndex((p) => p.width === v && p.height === formData.canvas_height);
    setSelectedPreset(match >= 0 ? match : CANVAS_PRESETS.length - 1);
  };

  const handleHeightChange = (v) => {
    onChange({ canvas_height: v });
    const match = CANVAS_PRESETS.findIndex((p) => p.width === formData.canvas_width && p.height === v);
    setSelectedPreset(match >= 0 ? match : CANVAS_PRESETS.length - 1);
  };

  const handleNameBlur = () => {
    if (!formData.name || !allLooks) {
      setNameWarning(null);
      return;
    }
    const duplicate = allLooks.find((l) => l.name === formData.name && l.id !== formData.id);
    setNameWarning(duplicate ? 'This name is already in use. Please choose a unique name.' : null);
  };

  // Clear name warning when name changes
  useEffect(() => {
    if (nameWarning) setNameWarning(null);
  }, [formData.name]);

  const errors = validationErrors || {};

  const handleRefPhoto = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => onChange({ ref_photo_data: ev.target.result });
    reader.readAsDataURL(file);
  };

  return (
    <div>
      <h4 style={{ margin: '0 0 12px', fontSize: 14, color: '#333' }}>Template Info</h4>

      <Field label="Name" error={errors.name || nameWarning}>
        <Input
          value={formData.name}
          onChange={(v) => onChange({ name: v })}
          placeholder="e.g. SPR26-01"
          error={!!errors.name || !!nameWarning}
          onBlur={handleNameBlur}
        />
      </Field>

      <Field label="Season" half error={errors.season}>
        <Select value={formData.season} onChange={(v) => onChange({ season: v })} options={SEASONS} />
      </Field>

      <Field label="Pose" half>
        <Select value={formData.pose_number} onChange={(v) => onChange({ pose_number: v })} options={POSE_NUMBERS} />
      </Field>

      <Field label="Type">
        <Select
          value={formData.look_type}
          onChange={(v) => {
            const patch = { look_type: v };
            // Seed the standard GIF-slide canvas size only for a NEW look that has
            // no dimensions yet — never overwrite an existing record's canvas.
            if (v === 'gif_slide' && !formData.canvas_width && !formData.canvas_height) {
              patch.canvas_width = GIF_SLIDE_DEFAULT_CANVAS.width;
              patch.canvas_height = GIF_SLIDE_DEFAULT_CANVAS.height;
            }
            // Leaving gif_slide clears the campaign FK so a non-gif look
            // never carries a dangling campaign_id (the model requires it
            // only for gif_slide).
            if (v !== 'gif_slide') patch.promo_gif_campaign_id = null;
            onChange(patch);
          }}
          options={[
            { value: 'single', label: 'Single' },
            { value: 'collage', label: 'Collage' },
            { value: 'gif_slide', label: 'GIF Slide' },
          ]}
        />
      </Field>

      {formData.look_type === 'gif_slide' && (
        <GifCampaignPicker
          season={formData.season}
          value={formData.promo_gif_campaign_id}
          onChange={(v) => onChange({ promo_gif_campaign_id: v })}
          error={errors.promo_gif_campaign_id}
        />
      )}

      <h4 style={{ margin: '16px 0 12px', fontSize: 14, color: '#333' }}>Canvas</h4>

      {/* A campaign is only ever one size — EC2 takes the gif's dimensions from
          the first slide and stretches the rest to match. Saying so here is what
          makes the campaign-wide save predictable instead of surprising. */}
      {formData.look_type === 'gif_slide' && formData.promo_gif_campaign_id && (
        <p style={{
          fontSize: 11, color: '#8a6116', margin: '-4px 0 10px', lineHeight: 1.5,
        }}
        >
          Changing this canvas changes
          {' '}
          <strong>every slide in this campaign</strong>
          {' '}
          — a GIF is one size throughout. Smaller canvas, smaller file: 640×480 keeps a
          3-slide campaign under the 600 KB an MMS can carry.
        </p>
      )}

      <Field label="Preset">
        <select
          value={selectedPreset}
          onChange={(e) => handlePresetChange(Number(e.target.value))}
          style={{
            width: '100%',
            padding: '6px 8px',
            border: '1px solid #ddd',
            borderRadius: 3,
            fontSize: 13,
            background: '#fff',
            boxSizing: 'border-box',
          }}
        >
          {CANVAS_PRESETS.map((p, i) => (
            <option key={i} value={i}>{p.label}</option>
          ))}
        </select>
      </Field>

      {selectedPreset === CANVAS_PRESETS.length - 1 && (
        <>
          <Field label="Width (px)" half error={errors.canvas_width}>
            <Input type="number" value={formData.canvas_width} onChange={handleWidthChange} error={!!errors.canvas_width} />
          </Field>
          <Field label="Height (px)" half error={errors.canvas_height}>
            <Input type="number" value={formData.canvas_height} onChange={handleHeightChange} error={!!errors.canvas_height} />
          </Field>
          <Field label="DPI" half error={errors.canvas_dpi}>
            <Input type="number" value={formData.canvas_dpi} onChange={(v) => onChange({ canvas_dpi: v })} error={!!errors.canvas_dpi} />
          </Field>
        </>
      )}

      {formData.look_type === 'single' && (
        <>
          <h4 style={{ margin: '16px 0 12px', fontSize: 14, color: '#333' }}>Source Pose Selection</h4>
          <p style={{ fontSize: 10, color: '#999', margin: '-8px 0 8px' }}>
            Which photo to use when a student has 3, 2, or 1 pose. Choose a shot number (1–3) or an existing look name.
          </p>

          <SourceField
            label="Source if 3 poses"
            value={formData.source_if_3}
            onChange={(v) => onChange({ source_if_3: v })}
            allLooks={allLooks}
            error={errors.source_if_3}
          />
          <SourceField
            label="Source if 2 poses"
            value={formData.source_if_2}
            onChange={(v) => onChange({ source_if_2: v })}
            allLooks={allLooks}
            error={errors.source_if_2}
          />
          <SourceField
            label="Source if 1 pose"
            value={formData.source_if_1}
            onChange={(v) => onChange({ source_if_1: v })}
            allLooks={allLooks}
            error={errors.source_if_1}
          />
        </>
      )}

      {(formData.look_type === 'collage' || formData.look_type === 'gif_slide') && (
        <>
          <h4 style={{ margin: '16px 0 12px', fontSize: 14, color: '#333' }}>Canvas Background</h4>

          <p style={{ fontSize: 10, color: '#999', margin: '0 0 8px' }}>
            Upload a reference image to preview the background while designing. Set the S3 path for production generation.
          </p>

          <Field label="Background Reference Image">
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files[0];
                if (!file) return;
                // Read base64 for immediate on-canvas preview.
                const reader = new FileReader();
                reader.onload = (ev) => onChange({ canvas_bg_ref_data: ev.target.result });
                reader.readAsDataURL(file);
                // Also push to S3 and record canvas_filepath / canvas_filename so
                // the background persists across reloads (list responses drop the
                // base64 preview) and reaches the print pipeline.
                uploadCanvasBg(file)
                  .then((res) => onChange({ canvas_filepath: res.s3_path, canvas_filename: res.filename }))
                  .catch((err) => {
                    // eslint-disable-next-line no-alert
                    alert(`Canvas background upload failed: ${err.response?.data?.error || err.message}. Set canvas_filepath / canvas_filename manually below, or try again.`);
                  });
              }}
              style={{ fontSize: 12 }}
            />
          </Field>
          {formData.canvas_bg_ref_data && (
            <div style={{ marginBottom: 8 }}>
              <img src={formData.canvas_bg_ref_data} style={{ maxWidth: 160, borderRadius: 4, border: '1px solid #ddd' }} />
              <button
                onClick={() => onChange({ canvas_bg_ref_data: null, canvas_filepath: null, canvas_filename: null })}
                style={{
                  display: 'block', marginTop: 4, fontSize: 11, color: '#d32f2f', background: 'none', border: 'none', cursor: 'pointer',
                }}
              >
                Remove
              </button>
            </div>
          )}

          <Field label="Canvas Filepath (S3 path)">
            <Input value={formData.canvas_filepath} onChange={(v) => onChange({ canvas_filepath: v })} placeholder="s3://bucket/path/" />
          </Field>
          <Field label="Canvas Filename">
            <Input value={formData.canvas_filename} onChange={(v) => onChange({ canvas_filename: v })} placeholder="background.jpg" />
          </Field>

          <CollagePhotosSection
            photos={formData.collage_photos || []}
            onChange={(photos) => onChange({ collage_photos: photos })}
            allLooks={allLooks}
            selectedPhotoIndex={formData._selectedCollagePhoto}
            onSelectPhoto={(idx) => onChange({ _selectedCollagePhoto: idx })}
            canvasWidth={formData.canvas_width}
            canvasHeight={formData.canvas_height}
            lookType={formData.look_type}
          />
        </>
      )}

      <h4 style={{ margin: '16px 0 12px', fontSize: 14, color: '#333' }}>Reference Photo</h4>
      <p style={{ fontSize: 11, color: '#999', margin: '0 0 8px' }}>
        Choose from pricelist reference poses or upload a custom image. Designer preview only — not used in generation.
      </p>

      {/* Pose picker — show available reference poses from the pricelist */}
      {referencePoses && referencePoses.some(Boolean) && (
        <div style={{ marginBottom: 8 }}>
          <div style={{
            fontSize: 11, color: '#666', marginBottom: 4, fontWeight: 500,
          }}
          >
            Choose from poses:
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {referencePoses.map((pose, i) => (pose ? (
              <div
                key={i}
                onClick={() => onChange({ ref_photo_data: pose })}
                style={{
                  cursor: 'pointer',
                  textAlign: 'center',
                  border: formData.ref_photo_data === pose ? '2px solid #1976d2' : '1px solid #ddd',
                  borderRadius: 4,
                  padding: 2,
                  background: formData.ref_photo_data === pose ? '#e3f2fd' : '#fff',
                }}
              >
                <img
                  src={pose}
                  style={{
                    width: 50, height: 62, objectFit: 'cover', borderRadius: 3, display: 'block',
                  }}
                />
                <div style={{ fontSize: 9, color: '#666', marginTop: 2 }}>
                  Shot
                  {i + 1}
                </div>
              </div>
            ) : null))}
          </div>
        </div>
      )}

      <div style={{ fontSize: 11, color: '#999', marginBottom: 4 }}>Or upload custom:</div>
      <input type="file" accept="image/*" onChange={handleRefPhoto} style={{ fontSize: 12 }} />
      {formData.ref_photo_data && (
        <div style={{ marginTop: 8 }}>
          <img src={formData.ref_photo_data} style={{ maxWidth: 120, borderRadius: 4, border: '1px solid #ddd' }} />
          <button
            onClick={() => onChange({ ref_photo_data: null })}
            style={{
              display: 'block', marginTop: 4, fontSize: 11, color: '#d32f2f', background: 'none', border: 'none', cursor: 'pointer',
            }}
          >
            Remove
          </button>
        </div>
      )}
    </div>
  );
};

export default LookTab;
